import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Switch,
  FormControlLabel,
  Autocomplete,
  Paper
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import UserDataService from "../pages/services/UserService";
import StampDutyService from "../pages/services/StampDutyService";
import { fNumber } from "../utils/formatNumber";

const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const clean = String(val).replace(/,/g, "").trim();
  const n = parseFloat(clean);
  return Number.isFinite(n) ? n : 0;
};

const formatInputWithCommas = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const clean = String(val).replace(/[^0-9.]/g, "");
  const parts = clean.split(".");
  const intPart = parts[0] ? Number(parts[0]).toLocaleString("en-US") : "";
  if (parts.length > 1) {
    return `${intPart}.${parts.slice(1).join("").slice(0, 2)}`;
  }
  return intPart;
};

const formatToCurrencyString = (val) => {
  const n = parseNum(val);
  if (n === 0 && (!val || String(val).trim() === "")) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function DevMinWelSplitModal({ open, onClose, onSuccess, initialMemberId }) {
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(initialMemberId || "");
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Form fields
  const [totalDeposit, setTotalDeposit] = useState("");
  const [welAmt, setWelAmt] = useState("");
  const [minAmt, setMinAmt] = useState("");
  const [devAmt, setDevAmt] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Stamp duty fields
  const [applyStampDuty, setApplyStampDuty] = useState(true);
  const [stampDutyAmount, setStampDutyAmount] = useState(50);
  const [deductDutyFromDeposit, setDeductDutyFromDeposit] = useState(true);
  const [waiveReason, setWaiveReason] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Load members on open
  useEffect(() => {
    if (!open) return;
    if (initialMemberId) {
      setSelectedMemberId(initialMemberId);
    }
    setLoadingMembers(true);
    UserDataService.getAll()
      .then((res) => {
        const list = res?.data || res || [];
        setMembers(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setLoadingMembers(false));
  }, [open, initialMemberId]);

  const memberOptions = useMemo(() => {
    return members
      .map((m) => {
        const id = String(m._id || m.id);
        const fname = (m.firstName || m.firstname || m.first_name || "").trim();
        const lname = (m.lastName || m.lastname || m.last_name || "").trim();
        const fullName = (lname ? `${lname} ${fname}`.trim() : fname) || (m.username || m.name || "").trim() || "Member";
        const regNo = (m.registrationNo || m.membershipId || "").trim();
        const label = regNo ? `${fullName} (${regNo})` : fullName;
        return {
          id,
          label,
          fullName,
          regNo,
          raw: m
        };
      })
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [members]);

  const selectedMemberOption = useMemo(() => {
    return memberOptions.find((opt) => opt.id === String(selectedMemberId)) || null;
  }, [memberOptions, selectedMemberId]);

  const selectedMember = useMemo(() => {
    return members.find((m) => String(m._id || m.id) === String(selectedMemberId)) || null;
  }, [members, selectedMemberId]);

  // Real-time calculations
  const totalDepNum = parseNum(totalDeposit);
  const wNum = parseNum(welAmt);
  const mNum = parseNum(minAmt);
  const dNum = parseNum(devAmt);
  const sdNum = applyStampDuty ? parseNum(stampDutyAmount) || 50 : 0;

  const dutyDeduction = deductDutyFromDeposit ? sdNum : 0;
  const netBalance = Math.max(0, Number((totalDepNum - dutyDeduction).toFixed(2)));

  const totalAllocated = wNum + mNum + dNum;
  const totalAccounted = totalAllocated + dutyDeduction;
  const remaining = Number((totalDepNum - totalAccounted).toFixed(2));

  const isDeficit = (totalDepNum > 0 || dutyDeduction > 0) && totalDepNum < dutyDeduction;
  const deficitAmount = isDeficit ? Number((dutyDeduction - totalDepNum).toFixed(2)) : 0;
  const isOverAllocated = totalAccounted > totalDepNum;
  const overAmount = isOverAllocated ? Number((totalAccounted - totalDepNum).toFixed(2)) : 0;

  const isBalanced =
    totalDepNum > 0 &&
    (Math.abs(remaining) <= 0.01 || (!applyStampDuty && Math.abs(remaining) <= 50.01)) &&
    !isDeficit &&
    !isOverAllocated;

  // Quick 3-Way Equal Split
  const handleEqualSplit = () => {
    if (netBalance <= 0) return;
    const third = Number((netBalance / 3).toFixed(2));
    const remainderThird = Number((netBalance - third * 2).toFixed(2)); // absorb round into Welfare
    setWelAmt(fNumber(remainderThird, 2));
    setMinAmt(fNumber(third, 2));
    setDevAmt(fNumber(third, 2));
  };

  // Quick Action: Absorb remaining unallocated into Welfare
  const handleAddRemainingToWelfare = () => {
    if (remaining === 0) return;
    const newWel = Math.max(0, Number((wNum + remaining).toFixed(2)));
    setWelAmt(fNumber(newWel, 2));
  };

  // Handler: Stamp duty toggle
  const handleToggleStampDuty = (checked) => {
    setApplyStampDuty(checked);
  };

  const handleStampDutyAmountChange = (val) => {
    setStampDutyAmount(val);
  };

  const handleSubmit = async () => {
    setError("");
    setSuccessMsg("");

    if (!selectedMemberId) {
      setError("Please select a member.");
      return;
    }
    if (totalDepNum <= 0) {
      setError("Please enter a valid total deposit amount.");
      return;
    }
    if (isDeficit) {
      setError(`Deposit amount (₦${fNumber(totalDepNum, 2)}) is less than duty deduction (₦${fNumber(dutyDeduction, 2)}). Deficit: ₦${fNumber(deficitAmount, 2)}.`);
      return;
    }
    if (isOverAllocated) {
      setError(`Total accounted (₦${fNumber(totalAccounted, 2)}) exceeds total deposit (₦${fNumber(totalDepNum, 2)}) by ₦${fNumber(overAmount, 2)}.`);
      return;
    }

    let finalWel = wNum;
    let finalAccounted = totalAccounted;
    let finalRemaining = remaining;

    // Auto-absorb ₦50 into Welfare if stamp duty is waived
    if (!applyStampDuty && Math.abs(remaining) > 0.01 && Math.abs(remaining) <= 50.01) {
      finalWel = Math.max(0, Number((wNum + remaining).toFixed(2)));
      finalAccounted = totalDepNum;
      finalRemaining = 0;
      setWelAmt(fNumber(finalWel, 2));
    }

    if (Math.abs(finalRemaining) > 0.01) {
      setError(`Total accounted (₦${fNumber(finalAccounted, 2)}) must equal total deposit (₦${fNumber(totalDepNum, 2)}). Unallocated remainder: ₦${fNumber(finalRemaining, 2)}.`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        memberId: selectedMemberId,
        depositAmount: totalDepNum,
        stampDutyAmount: applyStampDuty ? sdNum : 0,
        accountType: "devminwel_split",
        splitDistribution: {
          wel: finalWel,
          min: mNum,
          dev: dNum
        },
        isWaived: !applyStampDuty,
        waiveReason: !applyStampDuty ? waiveReason || "Waived: Single deposit split across Welfare, Minutes, Development" : "",
        date,
        notes:
          notes.trim() ||
          `Single deposit split across Welfare (₦${fNumber(finalWel, 2)}), Minutes (₦${fNumber(mNum, 2)}), Development (₦${fNumber(dNum, 2)})${
            dutyDeduction > 0 ? `, Stamp Duty (₦${fNumber(dutyDeduction, 2)})` : ""
          }`,
        autoCreditAccounts: true
      };

      const res = await StampDutyService.record(payload);
      setSuccessMsg(res?.data?.message || "Welfare, Minutes & Development Split successfully posted!");
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1200);
    } catch (err) {
      console.error("Welfare split deposit error:", err);
      setError(err?.response?.data?.error || err.message || "Failed to process split deposit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 2, sm: 3 },
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: "96vh", sm: "92vh" }
        }
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #1E293B 0%, #0F766E 50%, #0D9488 100%)",
          color: "#ffffff",
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <VolunteerActivismRoundedIcon sx={{ fontSize: 28, color: "#34D399" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: "1rem", sm: "1.2rem" }, lineHeight: 1.2 }}>
              Welfare &amp; Dues Split Tool (1x Stamp Duty)
            </Typography>
            <Typography variant="caption" sx={{ color: "#A7F3D0", display: "block" }}>
              One-time deposit posting shared between Welfare, Minutes &amp; Development
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#ffffff", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "#F8FAFC" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {successMsg}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Member Selection */}
          <Grid item xs={12} sm={8}>
            <Autocomplete
              options={memberOptions}
              getOptionLabel={(opt) => opt.label || ""}
              value={selectedMemberOption}
              onChange={(_, newVal) => setSelectedMemberId(newVal ? newVal.id : "")}
              loading={loadingMembers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Society Member *"
                  placeholder="Type name or registration number..."
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingMembers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* Date */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="date"
              label="Deposit Date *"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          {/* Member Current Dues Balances Strip */}
          {selectedMember && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#ffffff" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", display: "block", mb: 0.5 }}>
                  Current Member Dues Balances:
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#0D9488", fontWeight: 700 }}>
                      Welfare: ₦{fNumber(selectedMember.welBalance || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#0284C7", fontWeight: 700 }}>
                      Minutes: ₦{fNumber(selectedMember.minBalance || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#D97706", fontWeight: 700 }}>
                      Development: ₦{fNumber(selectedMember.devBalance || 0)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Total Bank Deposit Received */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Total Bank Deposit Received (₦) *"
              placeholder="e.g. 50,000.00"
              value={totalDeposit}
              onChange={(e) => setTotalDeposit(formatInputWithCommas(e.target.value))}
              onBlur={() => {
                if (totalDeposit) setTotalDeposit(formatToCurrencyString(totalDeposit));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ fontWeight: 800, color: "#0D9488" }}>
                    ₦
                  </InputAdornment>
                ),
                inputMode: "decimal"
              }}
              helperText="Enter full deposit amount (formatted with commas e.g. 50,000.00)"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  fontWeight: 800,
                  color: "#0F172A"
                }
              }}
              required
            />
          </Grid>

          {/* STAMP DUTY (EMTL) CONTROL */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #CBD5E1", backgroundColor: "#ffffff" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <VerifiedUserRoundedIcon sx={{ color: applyStampDuty ? "#0D9488" : "#94A3B8" }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1E293B" }}>
                    Stamp Duty (EMTL)
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch checked={applyStampDuty} onChange={(e) => handleToggleStampDuty(e.target.checked)} color="primary" />
                  }
                  label={applyStampDuty ? `Apply ₦${fNumber(stampDutyAmount || 50, 2)} Stamp Duty Once` : "Waive Stamp Duty"}
                  sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.85rem" } }}
                />
              </Box>

              {applyStampDuty ? (
                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                    Duty Amount:
                  </Typography>
                  {[50, 100, 200].map((preset) => (
                    <Button
                      key={preset}
                      size="small"
                      variant={String(stampDutyAmount) === String(preset) ? "contained" : "outlined"}
                      onClick={() => handleStampDutyAmountChange(preset)}
                      sx={{
                        minWidth: 64,
                        py: 0.3,
                        px: 1,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        borderRadius: "6px",
                        bgcolor: String(stampDutyAmount) === String(preset) ? "#0D9488" : "transparent",
                        color: String(stampDutyAmount) === String(preset) ? "#ffffff" : "#0D9488",
                        borderColor: "#0D9488"
                      }}
                    >
                      ₦{preset} {preset === 50 ? "(Std)" : ""}
                    </Button>
                  ))}
                  <TextField
                    size="small"
                    label="Custom Duty (₦)"
                    value={stampDutyAmount}
                    onChange={(e) => handleStampDutyAmountChange(formatInputWithCommas(e.target.value))}
                    onBlur={() => {
                      if (stampDutyAmount) setStampDutyAmount(formatToCurrencyString(stampDutyAmount));
                    }}
                    sx={{ width: 140, backgroundColor: "#ffffff" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={deductDutyFromDeposit}
                        onChange={(e) => setDeductDutyFromDeposit(e.target.checked)}
                        color="secondary"
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155" }}>
                        Deduct ₦{fNumber(sdNum, 2)} from this deposit before split
                      </Typography>
                    }
                  />
                </Box>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Reason for Waiving Stamp Duty"
                  value={waiveReason}
                  onChange={(e) => setWaiveReason(e.target.value)}
                  placeholder="e.g. Dues deposit already charged stamp duty on another receipt / Officer waiver"
                  sx={{ mt: 1.5, backgroundColor: "#ffffff" }}
                />
              )}
            </Paper>
          </Grid>

          {/* DUES ALLOCATION BREAKDOWN */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                Dues Allocation Breakdown:
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleEqualSplit}
                disabled={netBalance <= 0}
                startIcon={<AutoAwesomeRoundedIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  borderColor: "#0D9488",
                  color: "#0D9488"
                }}
              >
                Split Equally (3-Way)
              </Button>
            </Box>
          </Grid>

          {/* 1. Welfare & Emergency Relief */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Welfare & Emergency (₦)"
              placeholder="e.g. 20,000.00"
              value={welAmt}
              onChange={(e) => setWelAmt(formatInputWithCommas(e.target.value))}
              onBlur={() => {
                if (welAmt) setWelAmt(formatToCurrencyString(welAmt));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ fontWeight: 700, color: "#0D9488" }}>
                    ₦
                  </InputAdornment>
                ),
                inputMode: "decimal"
              }}
              helperText="Welfare portion"
            />
          </Grid>

          {/* 2. Minutes Account */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Minutes Account (₦)"
              placeholder="e.g. 15,000.00"
              value={minAmt}
              onChange={(e) => setMinAmt(formatInputWithCommas(e.target.value))}
              onBlur={() => {
                if (minAmt) setMinAmt(formatToCurrencyString(minAmt));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ fontWeight: 700, color: "#0284C7" }}>
                    ₦
                  </InputAdornment>
                ),
                inputMode: "decimal"
              }}
              helperText="Minutes dues portion"
            />
          </Grid>

          {/* 3. Development Account */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Development Account (₦)"
              placeholder="e.g. 15,000.00"
              value={devAmt}
              onChange={(e) => setDevAmt(formatInputWithCommas(e.target.value))}
              onBlur={() => {
                if (devAmt) setDevAmt(formatToCurrencyString(devAmt));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ fontWeight: 700, color: "#D97706" }}>
                    ₦
                  </InputAdornment>
                ),
                inputMode: "decimal"
              }}
              helperText="Development levy portion"
            />
          </Grid>

          {/* Allocation Balance Strip */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: isBalanced ? "#F0FDF4" : "#FEF2F2",
                border: "1.5px solid",
                borderColor: isBalanced ? "#86EFAC" : "#FECACA",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 800, color: isBalanced ? "#166534" : "#991B1B" }}>
                  Total Accounted: ₦{fNumber(totalAccounted, 2)} of ₦{fNumber(totalDepNum, 2)}
                </Typography>
                <Typography variant="caption" sx={{ color: isBalanced ? "#15803D" : "#B91C1C", display: "block" }}>
                  (Welfare: ₦{fNumber(wNum, 2)} + Minutes: ₦{fNumber(mNum, 2)} + Development: ₦{fNumber(dNum, 2)}
                  {dutyDeduction > 0 ? ` + EMTL: ₦${fNumber(dutyDeduction, 2)}` : ""})
                </Typography>
              </Box>

              <Chip
                icon={
                  isBalanced ? (
                    <CheckCircleRoundedIcon sx={{ color: "#166534 !important" }} />
                  ) : (
                    <WarningAmberRoundedIcon sx={{ color: "#991B1B !important" }} />
                  )
                }
                label={
                  isBalanced
                    ? "✓ 100% Balanced"
                    : isDeficit
                    ? `Deficit: −₦${fNumber(deficitAmount, 2)}`
                    : isOverAllocated
                    ? `Over-Allocated: +₦${fNumber(overAmount, 2)}`
                    : `Remaining: ₦${fNumber(remaining, 2)}`
                }
                sx={{
                  fontWeight: 900,
                  fontSize: "0.85rem",
                  backgroundColor: isBalanced ? "#DCFCE7" : "#FEE2E2",
                  color: isBalanced ? "#166534" : "#991B1B",
                  borderColor: isBalanced ? "#86EFAC" : "#FCA5A5",
                  border: "1px solid"
                }}
              />

              {!isBalanced && remaining > 0 && (
                <Box
                  sx={{
                    width: "100%",
                    mt: 1,
                    pt: 1,
                    borderTop: "1px dashed #FECACA",
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap"
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#991B1B" }}>
                    Quick Balance:
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleAddRemainingToWelfare}
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 800,
                      textTransform: "none",
                      borderColor: "#0D9488",
                      color: "#0D9488",
                      backgroundColor: "#F0FDF4",
                      py: 0.25,
                      "&:hover": { backgroundColor: "#DCFCE7" }
                    }}
                  >
                    + Add ₦{fNumber(remaining, 2)} to Welfare
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Transaction Notes / Bank Reference (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Welfare & Dues bank transfer #REF-12345"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, sm: 2.5 },
          backgroundColor: "#F8FAFC",
          borderTop: "1px solid #E2E8F0",
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: { xs: 1, sm: 1.5 }
        }}
      >
        <Button onClick={onClose} disabled={submitting} sx={{ fontWeight: 700, width: { xs: "100%", sm: "auto" } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || totalDepNum <= 0 || !isBalanced || isDeficit || isOverAllocated}
          startIcon={submitting ? <CircularProgress size={18} sx={{ color: "#ffffff" }} /> : <CheckCircleRoundedIcon />}
          sx={{
            backgroundColor: "#0D9488",
            fontWeight: 800,
            textTransform: "none",
            px: 3,
            py: 1,
            width: { xs: "100%", sm: "auto" },
            boxShadow: "0 4px 12px rgba(13, 148, 136, 0.25)",
            "&:hover": { backgroundColor: "#0F766E" },
            "&.Mui-disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" }
          }}
        >
          {submitting
            ? "Posting..."
            : isDeficit
            ? "Insufficient Deposit"
            : isOverAllocated
            ? "Over-Allocated"
            : "Confirm & Post Welfare Dues Split"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DevMinWelSplitModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  initialMemberId: PropTypes.string
};
